import React, { useEffect, useState } from "react";
import axios from "axios";
import logo from "./bbs_bracu_logo_white.png";
import img from "./all_bg.jpg";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [facultyList, setFacultyList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        if (!token) throw new Error("Not logged in");

        const res = await axios.get(
          "https://api.bbsfacultyprofile.com/api/faculty",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setFacultyList(
          res.data.map((f) => ({
            pin: f.pin,
            name: f.name || null,
            email: f.email || null,
          }))
        );
      } catch (err) {
        console.error(err);
        toast.error("Access denied or session expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchFaculty();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    toast.success("Logging out...");
    setTimeout(() => (window.location.href = "/"), 1000);
  };

  const handleDownloadCSV = async () => {
    if (!token) {
      toast.warning("You need to login first!");
      return navigate("/login");
    }

    try {
      const response = await axios.get(
        "https://api.bbsfacultyprofile.com/api/faculty/download-csv",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "faculty_full_profile.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Downloading all faculty members' data...");
    } catch (err) {
      console.error(err);
      toast.error("Download failed. Authorization error.");
    }
  };

  const handleGenerateToken = async (email) => {
    try {
      const res = await axios.post(
        "https://api.bbsfacultyprofile.com/api/admin/generate-token",
        { email },
        {
          headers: { Authorization: `Bearer ${token}` }, // admin token
        }
      );
      toast.success(`Reset token for ${email}: ${res.data.token}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate reset token.");
    }
  };

  const filteredFaculty = facultyList
    .filter(
      (f) =>
        f.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.pin.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      const nameA = (a.name || a.email || "").toLowerCase();
      const nameB = (b.name || b.email || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

  if (loading)
    return <p style={{ textAlign: "center", padding: "50px" }}>Loading...</p>;

  return (
    <div
      style={{ position: "relative", minHeight: "100vh", fontFamily: "Arial" }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <style>
        {`
    .faculty-row:hover {
      background-color: #9ab7e17c !important; /* light blue hover color */
      transition: background-color 0.2s ease;
      cursor: pointer;
    }
  `}
      </style>

      {/* Background */}
      <div
        style={{
          position: "fixed",
          top: "-50px",
          left: 0,
          width: "100%",
          height: "110%",
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top center",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Navbar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#152e74",
          color: "white",
          padding: "0.5px 20px",
          flexWrap: "wrap",
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          <img
            src={logo}
            alt="BBS Logo"
            style={{ height: "120px", width: "auto", objectFit: "contain" }}
          />
        </div>
        <div style={{ flex: 1, textAlign: "center" }}>
          <h2
            style={{
              margin: 0,
              fontSize: "1.8rem",
              fontFamily: "Georgia, serif",
            }}
          >
            Admin Dashboard
          </h2>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexShrink: 0,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            onClick={handleDownloadCSV}
            style={{
              background: "#4caf50",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "6px 12px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Download All Faculty Data
          </button>
          <button
            onClick={handleLogout}
            style={{
              background: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "6px 12px",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          paddingTop: "150px",
          width: "95%",
          margin: "0 auto",
          paddingBottom: "40px",
          boxSizing: "border-box",
        }}
      >
        {/* Search Bar */}
        <div style={{ marginBottom: "20px" }}>
          <input
            type="text"
            placeholder="Search by name, email, or pin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "1rem",
              borderRadius: "8px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        {/* Faculty List */}
        <div
          style={{
            width: "100%",
            maxHeight: "calc(100vh - 220px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "8px 12px",
              fontWeight: "bold",
              borderBottom: "2px solid #333",
              fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              background: "#f0f4f8",
            }}
          >
            <span>Name / Email</span>
            <span>PIN / Actions</span>
          </div>

          {/* Rows */}
          {filteredFaculty.length === 0 ? (
            <p style={{ padding: "10px" }}>No faculty data available.</p>
          ) : (
            filteredFaculty.map(({ pin, name, email }, index) => (
              <div
                key={pin}
                className="faculty-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: index % 2 === 0 ? "#ffffff" : "#f9f9f9",
                  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
                  fontSize: "0.95rem",
                  alignItems: "center",
                }}
              >
                <span onClick={() => navigate(`/faculty/${pin}`)}>
                  {name || email}
                </span>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <span style={{ fontFamily: "monospace", color: "#555" }}>
                    {pin}
                  </span>
                  <button
                    onClick={() => handleGenerateToken(email)}
                    style={{
                      background: "#f39c12",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      padding: "4px 8px",
                      cursor: "pointer",
                    }}
                  >
                    Allow Password Reset
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <footer
        style={{
          textAlign: "center",
          padding: "20px 0",
          backgroundColor: "transparent",
          color: "black",
        }}
      >
        <p>Copyright © 2025 - All rights reserved by BRAC Business School</p>
        <p>
          Developed By Refah Tasnia, Research Associate, BRAC Business School
        </p>
      </footer>
    </div>
  );
}
